import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Avatar,
  Chip,
  Typography,
  Button,
  Divider,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { Eye } from "iconsax-react";
import ReusableRightDrawer from "./ReusableRightDrawer";
import InfoActionIcons from "./InfoActionIcons";
import {
  dedupeHiringManagerRows,
  fetchHiringManagers,
  isCompanyContactRow,
  isHiringManagerContactRow,
} from "../utils/hiringManagers";
import { normalizeContactRoleLabel } from "../utils/contactRoles";
import { buildCompanyProfileActions, getCompanySocialLinksArray } from "../utils/companySocialLinks";
import {
  buildOpenCompanyNavEntity,
  buildOpenCompanyNavPayload,
} from "../utils/companyNavigation";
import { formatLocalDate, parseDateTimeValue } from "../utils/dateTime";
import { formatRelativeTimeShort } from "../utils/relativeTime";
import {
  normalizeContactEmail,
  normalizeContactImageUrl,
  normalizeContactPhone,
  normalizeContactSocialUrl,
} from "../utils/contactChannels";
import {
  areSamePersonNames,
  getRecruiterOwnerName,
  isRecruiterContact,
} from "../utils/recruiterOwnership";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const NAV_CACHE_KEY = "rpo:openCompanyNav";
const DRAWER_CACHE_TTL_MS = 45 * 1000;
const DRAWER_CACHE_MAX_ENTRIES = 60;
const COMPANY_CONTACT_REPAIR_REFRESH_DELAY_MS = 5000;
const detailResponseCache = new Map();
const hiringManagersResponseCache = new Map();
const linkedCompanySearchCache = new Map();
const linkedCompanyBundleCache = new Map();

function safeStr(value, fallback = "") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function formatDateTimeLabel(value) {
  return formatRelativeTimeShort(value, { emptyValue: "No recent activity" });
}

function formatCreatedLabel(value) {
  if (!value) return "Created: -";

  const date = parseDateTimeValue(value);
  if (Number.isNaN(date.getTime())) return `Created: ${value}`;

  return `Created: ${formatLocalDate(date, {
    weekday: "short",
    day: "numeric",
    month: "short",
  })}`;
}

function formatWebsiteLabel(value) {
  const raw = safeStr(value);
  if (!raw) return "-";

  return raw
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/, "");
}

function truncateText(value, max = 130) {
  const text = safeStr(value);
  if (!text) return "";
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}...`;
}

function getEntityId(company, entityType = "lead") {
  const normalizedEntityType = String(entityType || "lead").toLowerCase();
  const candidates =
    normalizedEntityType === "company"
      ? [
          company?.id,
          company?.company_id,
          company?.companyId,
          company?.lead_id,
          company?.leadId,
        ]
      : [
          company?.id,
          company?.lead_id,
          company?.leadId,
          company?.company_id,
          company?.companyId,
        ];

  for (const candidate of candidates) {
    if (candidate !== null && candidate !== undefined && candidate !== "") {
      return candidate;
    }
  }

  return null;
}

function getLinkedCompanyId(company) {
  const value = company?.company_id ?? company?.companyId ?? null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function normalizeLookupValue(value) {
  return safeStr(value).toLowerCase().replace(/\s+/g, " ").trim();
}

function getUrlHostname(value) {
  const raw = safeStr(value);
  if (!raw) return "";

  const normalizedUrl = /^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`;

  try {
    return new URL(normalizedUrl).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return "";
  }
}

function getEntityCacheKey(entityType, entityId) {
  const normalizedEntityType = safeStr(entityType).toLowerCase();
  const normalizedEntityId = safeStr(entityId);
  if (!normalizedEntityType || !normalizedEntityId) return "";
  return `${normalizedEntityType}:${normalizedEntityId}`;
}

function getLinkedCompanyLookupCacheKey({
  companyId = null,
  companyName = "",
  website = "",
} = {}) {
  const normalizedCompanyId = Number(companyId);
  if (Number.isFinite(normalizedCompanyId) && normalizedCompanyId > 0) {
    return `company:${normalizedCompanyId}`;
  }

  const normalizedName = normalizeLookupValue(companyName);
  const normalizedWebsiteHost = getUrlHostname(website);
  if (!normalizedName && !normalizedWebsiteHost) return "";

  return `lookup:${normalizedName}::${normalizedWebsiteHost}`;
}

function readCachedValue(cache, key) {
  if (!key) return undefined;

  const entry = cache.get(key);
  if (!entry) return undefined;

  if (Date.now() - entry.timestamp > DRAWER_CACHE_TTL_MS) {
    cache.delete(key);
    return undefined;
  }

  return entry.value;
}

function writeCachedValue(cache, key, value) {
  if (!key) return value;

  if (cache.has(key)) {
    cache.delete(key);
  }

  cache.set(key, {
    value,
    timestamp: Date.now(),
  });

  while (cache.size > DRAWER_CACHE_MAX_ENTRIES) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }

  return value;
}

function findMatchingCompanyOption(options = [], { companyName = "", website = "" } = {}) {
  const normalizedName = normalizeLookupValue(companyName);
  const websiteHost = getUrlHostname(website);

  if (websiteHost) {
    const byWebsite = options.find((option) => {
      const optionWebsite =
        option?.website ||
        option?.company_website ||
        option?.companyWebsite ||
        option?.company_link ||
        option?.career_site ||
        option?.career_website ||
        "";
      return getUrlHostname(optionWebsite) === websiteHost;
    });
    if (byWebsite) return byWebsite;
  }

  if (!normalizedName) return null;

  const exact = options.find(
    (option) =>
      normalizeLookupValue(option?.name || option?.company_name || option?.companyName) ===
      normalizedName
  );
  if (exact) return exact;

  return (
    options.find((option) => {
      const optionName = normalizeLookupValue(
        option?.name || option?.company_name || option?.companyName
      );
      return optionName && (optionName.includes(normalizedName) || normalizedName.includes(optionName));
    }) || null
  );
}

function isRecruiterLikeEntry(item = {}, recruiterName = "") {
  const name = safeStr(item?.name || item?.full_name || item?.contact_name);
  if (name && recruiterName && areSamePersonNames(name, recruiterName)) return true;
  return isRecruiterContact(item);
}

function buildContactChannelActions(contact = {}) {
  return [
    contact?.linkedin_url
      ? {
          type: "linkedin",
          href: contact.linkedin_url,
          title: `${contact.name || "Contact"} LinkedIn`,
        }
      : null,
    contact?.email
      ? {
          type: "email",
          href: contact.email,
          title: `${contact.name || "Contact"} email`,
        }
      : null,
    contact?.phone
      ? {
          type: "phone",
          href: contact.phone,
          title: `${contact.name || "Contact"} phone`,
        }
      : null,
  ].filter(Boolean);
}

function buildContacts(company, recruiterName = "") {
  const explicit = Array.isArray(company?.contacts) ? company.contacts : [];
  if (explicit.length) {
    const seen = new Set();
    return explicit
      .map((item) => ({
        id: item?.id ?? null,
        name: safeStr(
          item?.name || item?.full_name || item?.email || item?.phone,
          "-"
        ),
        role: normalizeContactRoleLabel(
          item?.role || item?.title || item?.position,
          {
            contactKind: item?.contact_kind,
            source: item?.source,
            fallback: "Primary Contact",
          }
        ),
        email: normalizeContactEmail(item?.email),
        phone: normalizeContactPhone(item?.phone),
        linkedin_url: normalizeContactSocialUrl(
          "linkedin",
          item?.linkedin_url || item?.linkedin
        ),
        profile_image: normalizeContactImageUrl(
          item?.profile_image || item?.avatar_url || item?.avatar || item?.photo_url || item?.image
        ),
        contact_kind: safeStr(item?.contact_kind),
      }))
      .filter((item) => item.name !== "-" && !isRecruiterLikeEntry(item, recruiterName))
      .filter((item) => {
        const key =
          item.id != null && item.id !== ""
            ? `id:${item.id}`
            : `${item.name.toLowerCase()}::${item.role.toLowerCase()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }

  const fallbackName =
    safeStr(company?.contact_name) ||
    safeStr(company?.contact_email) ||
    safeStr(company?.contact_phone);

  if (!fallbackName || (recruiterName && areSamePersonNames(fallbackName, recruiterName))) {
    return [];
  }

  return [
    {
      name: fallbackName,
      role: normalizeContactRoleLabel(company?.contact_role || company?.contact_title, {
        fallback: "Primary Contact",
      }),
      email: normalizeContactEmail(company?.contact_email),
      phone: normalizeContactPhone(company?.contact_phone),
      linkedin_url: normalizeContactSocialUrl("linkedin", company?.linkedin_url),
      profile_image: "",
    },
  ];
}

function mergeUniqueContacts(contactLists = []) {
  const seen = new Set();

  return (Array.isArray(contactLists) ? contactLists : [])
    .flatMap((list) => (Array.isArray(list) ? list : []))
    .filter((item) => item && safeStr(item?.name))
    .filter((item) => {
      const key =
        item?.id != null && item.id !== ""
          ? `id:${item.id}`
          : [
              safeStr(item?.name).toLowerCase(),
              safeStr(item?.role).toLowerCase(),
              safeStr(item?.contact_kind).toLowerCase(),
            ].join("::");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function buildJobs(company) {
  const explicit = Array.isArray(company?.jobs) ? company.jobs : [];
  if (explicit.length) {
    return explicit
      .map((item) => {
        if (typeof item === "string") {
          return { title: safeStr(item), location: safeStr(company?.location) };
        }

        return {
          title: safeStr(
            item?.title || item?.job_title || item?.name || item?.label,
            ""
          ),
          location: safeStr(item?.location || item?.city || item?.country || company?.location),
        };
      })
      .filter((item) => item.title);
  }

  const authoritativeJobsCount = Number(company?.jobsCount ?? company?.jobs_count);
  if (Number.isFinite(authoritativeJobsCount)) {
    const normalizedJobsCount = Math.max(0, authoritativeJobsCount);
    if (normalizedJobsCount <= 0) return [];

    return [
      {
        title:
          normalizedJobsCount === 1
            ? "1 open role"
            : `${normalizedJobsCount} open roles`,
        location: safeStr(company?.location),
      },
    ];
  }

  const openings = Number(company?.job_openings ?? company?.jobOpenings ?? 0);
  if (!Number.isFinite(openings) || openings <= 0) return [];

  return [
    {
      title: openings === 1 ? "1 open role" : `${openings} open roles`,
      location: safeStr(company?.location),
    },
  ];
}

function buildFallbackHiringManagers(company, recruiterName = "") {
  const explicit = dedupeHiringManagerRows(
    Array.isArray(company?.hiringManagers)
      ? company.hiringManagers
      : Array.isArray(company?.hiring_managers)
      ? company.hiring_managers
      : []
  );

  const sanitizedExplicit = explicit.filter(
    (item) => !isRecruiterLikeEntry(item, recruiterName)
  );

  if (sanitizedExplicit.length) return sanitizedExplicit;

  const fallbackName = safeStr(company?.hiring_manager_name || company?.hiring_manager);
  if (!fallbackName || (recruiterName && areSamePersonNames(fallbackName, recruiterName))) {
    return [];
  }

  return [
    {
      name: fallbackName,
      role: safeStr(company?.hiring_manager_role || company?.hiring_manager_title, "Hiring Manager"),
    },
  ];
}

export default function CompanyProfileDrawer({
  open,
  onClose,
  company,
  onShowMore,
  entityType = "lead",
  allowShowMore = true,
}) {
  const navigate = useNavigate();
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hiringManagers, setHiringManagers] = useState([]);
  const [hiringManagersLoading, setHiringManagersLoading] = useState(false);
  const [linkedCompanyId, setLinkedCompanyId] = useState(null);
  const [linkedCompanyProfile, setLinkedCompanyProfile] = useState(null);
  const [linkedCompanyHiringManagers, setLinkedCompanyHiringManagers] = useState([]);
  const [linkedCompanyLoading, setLinkedCompanyLoading] = useState(false);
  const [detailReloadKey, setDetailReloadKey] = useState(0);
  const [contactsReloadKey, setContactsReloadKey] = useState(0);
  const pendingContactRepairRefreshesRef = useRef(0);

  const normalizedEntityType = String(entityType || "lead").toLowerCase();
  const entityId = useMemo(
    () => getEntityId(company, normalizedEntityType),
    [company, normalizedEntityType]
  );
  const entityCacheKey = useMemo(
    () => getEntityCacheKey(normalizedEntityType, entityId),
    [entityId, normalizedEntityType]
  );
  const canShowMore = Boolean(allowShowMore);

  useEffect(() => {
    pendingContactRepairRefreshesRef.current = 0;
    setDetailReloadKey(0);
    setContactsReloadKey(0);
  }, [entityId, normalizedEntityType, open]);

  useEffect(() => {
    const refreshApprovedReviewRecords = (event) => {
      const updatedCompanyId = Number(event?.detail?.companyId || 0);
      if (
        normalizedEntityType !== "company" ||
        !Number.isFinite(updatedCompanyId) ||
        updatedCompanyId !== Number(entityId)
      ) {
        return;
      }

      detailResponseCache.delete(entityCacheKey);
      hiringManagersResponseCache.delete(entityCacheKey);
      setDetailReloadKey((previous) => previous + 1);
      setContactsReloadKey((previous) => previous + 1);
    };

    window.addEventListener("rpo:company-review-records-updated", refreshApprovedReviewRecords);
    return () => {
      window.removeEventListener("rpo:company-review-records-updated", refreshApprovedReviewRecords);
    };
  }, [entityCacheKey, entityId, normalizedEntityType]);

  useEffect(() => {
    if (!open || !entityId) {
      if (!open) {
        setLeadData(null);
        setLoading(false);
        setError("");
      }
      return undefined;
    }

    const cachedDetail =
      detailReloadKey === 0
        ? readCachedValue(detailResponseCache, entityCacheKey)
        : undefined;

    if (cachedDetail?.data) {
      setLeadData(cachedDetail.data);
      setError("");

      if (!cachedDetail.contactRepairPending) {
        setLoading(false);
        return undefined;
      }
    } else {
      setLeadData(null);
    }

    const controller = new AbortController();
    let active = true;
    let pendingRefreshTimeoutId = null;

    setLoading(true);
    setError("");

    const detailUrl =
      normalizedEntityType === "company"
        ? `${API_BASE}/api/companies/${entityId}`
        : `${API_BASE}/api/leads/${entityId}`;

    fetch(detailUrl, {
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const message = await response.text().catch(() => "");
          throw new Error(message || `Failed to fetch lead ${entityId}`);
        }
        return response.json();
      })
      .then((payload) => {
        const contactRepairPending = Boolean(payload?.contact_repair_pending);
        const nextLeadData =
          payload?.company || payload?.lead || payload || null;
        const nextLeadDataWithPending =
          nextLeadData && typeof nextLeadData === "object"
            ? {
                ...nextLeadData,
                contact_repair_pending: contactRepairPending,
              }
            : nextLeadData;
        if (!active) return;
        setLeadData(nextLeadDataWithPending);
        writeCachedValue(detailResponseCache, entityCacheKey, {
          data: nextLeadDataWithPending,
          contactRepairPending,
        });
        if (normalizedEntityType === "company" && contactRepairPending) {
          hiringManagersResponseCache.delete(entityCacheKey);
          setContactsReloadKey((prev) => prev + 1);
          pendingContactRepairRefreshesRef.current = 1;
          pendingRefreshTimeoutId = window.setTimeout(() => {
            if (!active) return;
            hiringManagersResponseCache.delete(entityCacheKey);
            setDetailReloadKey((prev) => prev + 1);
            setContactsReloadKey((prev) => prev + 1);
          }, COMPANY_CONTACT_REPAIR_REFRESH_DELAY_MS);
        } else if (!contactRepairPending) {
          if (pendingContactRepairRefreshesRef.current > 0) {
            hiringManagersResponseCache.delete(entityCacheKey);
            setContactsReloadKey((prev) => prev + 1);
          }
          pendingContactRepairRefreshesRef.current = 0;
        }
      })
      .catch((fetchError) => {
        if (!active || fetchError?.name === "AbortError") return;
        console.error("Failed to fetch company profile:", fetchError);
        setError("Unable to load the latest profile data.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      if (pendingRefreshTimeoutId) {
        window.clearTimeout(pendingRefreshTimeoutId);
      }
      controller.abort();
    };
  }, [detailReloadKey, entityCacheKey, open, entityId, normalizedEntityType]);

  useEffect(() => {
    if (!open || !entityId) {
      if (!open) {
        setHiringManagers([]);
        setHiringManagersLoading(false);
      }
      return undefined;
    }

    const cachedHiringManagers =
      contactsReloadKey === 0
        ? readCachedValue(hiringManagersResponseCache, entityCacheKey)
        : undefined;

    if (cachedHiringManagers !== undefined) {
      setHiringManagers(cachedHiringManagers);
      setHiringManagersLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setHiringManagers([]);
    setHiringManagersLoading(true);

    fetchHiringManagers({
      apiBase: API_BASE,
      entityType: normalizedEntityType,
      entityId,
      signal: controller.signal,
    })
      .then((rows) => {
        if (!active) return;
        writeCachedValue(hiringManagersResponseCache, entityCacheKey, rows);
        setHiringManagers(rows);
      })
      .catch((fetchError) => {
        if (!active || fetchError?.name === "AbortError") return;
        console.error("Failed to fetch company hiring managers:", fetchError);
      })
      .finally(() => {
        if (active) setHiringManagersLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [contactsReloadKey, entityCacheKey, open, entityId, normalizedEntityType]);

  const profile = useMemo(
    () => ({ ...(company || {}), ...(leadData || {}) }),
    [company, leadData]
  );
  const lookupCompanyName = useMemo(
    () => safeStr(profile.company_name || profile.name || company?.company_name || company?.name),
    [company?.company_name, company?.name, profile.company_name, profile.name]
  );
  const lookupCompanyWebsite = useMemo(
    () =>
      safeStr(
        profile.website ||
          profile.company_website ||
          profile.website_url ||
          company?.website ||
          company?.company_website ||
          company?.website_url
      ),
    [
      company?.company_website,
      company?.website,
      company?.website_url,
      profile.company_website,
      profile.website,
      profile.website_url,
    ]
  );
  const directLinkedCompanyId = useMemo(
    () => getLinkedCompanyId(profile) ?? getLinkedCompanyId(company),
    [company, profile]
  );
  const linkedCompanyLookupCacheKey = useMemo(
    () =>
      getLinkedCompanyLookupCacheKey({
        companyId: directLinkedCompanyId,
        companyName: lookupCompanyName,
        website: lookupCompanyWebsite,
      }),
    [directLinkedCompanyId, lookupCompanyName, lookupCompanyWebsite]
  );

  useEffect(() => {
    if (!open || normalizedEntityType !== "lead") {
      setLinkedCompanyId(null);
      setLinkedCompanyProfile(null);
      setLinkedCompanyHiringManagers([]);
      setLinkedCompanyLoading(false);
      return undefined;
    }

    if (!directLinkedCompanyId && !lookupCompanyName && !lookupCompanyWebsite) {
      setLinkedCompanyId(null);
      setLinkedCompanyProfile(null);
      setLinkedCompanyHiringManagers([]);
      setLinkedCompanyLoading(false);
      return undefined;
    }

    const cachedResolvedCompanyId =
      directLinkedCompanyId ??
      readCachedValue(linkedCompanySearchCache, linkedCompanyLookupCacheKey) ??
      null;
    const cachedLinkedCompanyBundle =
      cachedResolvedCompanyId != null
        ? readCachedValue(
            linkedCompanyBundleCache,
            String(cachedResolvedCompanyId)
          )
        : undefined;

    if (cachedLinkedCompanyBundle) {
      setLinkedCompanyId(cachedLinkedCompanyBundle.companyId);
      setLinkedCompanyProfile(cachedLinkedCompanyBundle.profile);
      setLinkedCompanyHiringManagers(cachedLinkedCompanyBundle.hiringManagers);
      setLinkedCompanyLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setLinkedCompanyId(null);
    setLinkedCompanyProfile(null);
    setLinkedCompanyHiringManagers([]);
    setLinkedCompanyLoading(true);

    const loadLinkedCompany = async () => {
      try {
        let resolvedCompanyId = cachedResolvedCompanyId;

        if (!resolvedCompanyId) {
          const searchTerm = lookupCompanyName || lookupCompanyWebsite;
          const searchResponse = await fetch(
            `${API_BASE}/api/companies?search=${encodeURIComponent(
              searchTerm
            )}&limit=25&include_contact_hydration=0`,
            {
              credentials: "include",
              signal: controller.signal,
            }
          );

          const searchPayload = await searchResponse.json().catch(() => ({}));
          if (!searchResponse.ok) {
            throw new Error(
              searchPayload?.error || `Failed to search companies (${searchResponse.status})`
            );
          }

          const matchedCompany = findMatchingCompanyOption(
            Array.isArray(searchPayload?.rows) ? searchPayload.rows : [],
            {
              companyName: lookupCompanyName,
              website: lookupCompanyWebsite,
            }
          );
          const matchedCompanyId = Number(
            matchedCompany?.id ?? matchedCompany?.company_id ?? matchedCompany?.companyId
          );
          resolvedCompanyId =
            Number.isFinite(matchedCompanyId) && matchedCompanyId > 0
              ? matchedCompanyId
              : null;

          if (resolvedCompanyId) {
            writeCachedValue(
              linkedCompanySearchCache,
              linkedCompanyLookupCacheKey,
              resolvedCompanyId
            );
          }
        }

        if (!resolvedCompanyId) return;

        const cachedBundle = readCachedValue(
          linkedCompanyBundleCache,
          String(resolvedCompanyId)
        );
        if (cachedBundle) {
          if (!active) return;
          setLinkedCompanyId(cachedBundle.companyId);
          setLinkedCompanyProfile(cachedBundle.profile);
          setLinkedCompanyHiringManagers(cachedBundle.hiringManagers);
          return;
        }

        const [companyResponse, companyHiringManagerRows] = await Promise.all([
          fetch(`${API_BASE}/api/companies/${encodeURIComponent(String(resolvedCompanyId))}`, {
            credentials: "include",
            signal: controller.signal,
          }),
          fetchHiringManagers({
            apiBase: API_BASE,
            entityType: "company",
            entityId: resolvedCompanyId,
            signal: controller.signal,
          }).catch(() => []),
        ]);

        const companyPayload = await companyResponse.json().catch(() => ({}));
        if (!companyResponse.ok) {
          throw new Error(
            companyPayload?.error || `Failed to fetch company ${resolvedCompanyId}`
          );
        }

        const nextLinkedCompanyBundle = {
          companyId: resolvedCompanyId,
          profile: companyPayload?.company || companyPayload || null,
          hiringManagers: dedupeHiringManagerRows(
            Array.isArray(companyHiringManagerRows) ? companyHiringManagerRows : []
          ),
        };

        if (!active) return;

        writeCachedValue(
          linkedCompanyBundleCache,
          String(resolvedCompanyId),
          nextLinkedCompanyBundle
        );
        setLinkedCompanyId(nextLinkedCompanyBundle.companyId);
        setLinkedCompanyProfile(nextLinkedCompanyBundle.profile);
        setLinkedCompanyHiringManagers(nextLinkedCompanyBundle.hiringManagers);
      } catch (fetchError) {
        if (!active || fetchError?.name === "AbortError") return;
        console.error("Failed to load linked company profile for lead drawer:", fetchError);
      } finally {
        if (active) setLinkedCompanyLoading(false);
      }
    };

    loadLinkedCompany();

    return () => {
      active = false;
      controller.abort();
    };
  }, [
    directLinkedCompanyId,
    linkedCompanyLookupCacheKey,
    lookupCompanyName,
    lookupCompanyWebsite,
    normalizedEntityType,
    open,
  ]);

  const recruiterName = useMemo(() => getRecruiterOwnerName(profile), [profile]);
  const linkedCompanyRecruiterName = useMemo(
    () => getRecruiterOwnerName(linkedCompanyProfile || {}),
    [linkedCompanyProfile]
  );

  const companyName = safeStr(profile.company_name || profile.name, "Company");
  const companyLogo = /^(null|undefined|false)$/i.test(String(profile.company_logo || profile.logo || "").trim())
    ? ""
    : String(profile.company_logo || profile.logo || "").trim();
  const websiteHref = safeStr(
    profile.website || profile.company_website || profile.website_url
  );
  const websiteLabel = formatWebsiteLabel(websiteHref);
  const industryText = safeStr(
    profile.industry || profile.industry_sector,
    "-"
  );
  const statusText = safeStr(profile.status, "-");
  const ownerName = safeStr(
    profile.owner_name || profile.owner,
    "-"
  );
  const sourceText = safeStr(profile.source, "-");
  const createdLabel = formatCreatedLabel(
    profile.created_at || profile.createdAt
  );
  const lastActivityTime = formatDateTimeLabel(
    profile.last_activity_time || profile.updated_at || profile.created_at
  );
  const lastActivityTitle = safeStr(
    profile.last_activity_type,
    "No recent activity recorded"
  );
  const lastActivityMessage = truncateText(
    profile.last_activity_message ||
      profile.last_activity_detail ||
      profile.full_company_description ||
      (profile.contact_email
        ? `Contact: ${profile.contact_email}`
        : sourceText !== "-"
        ? `Source: ${sourceText}`
        : ""),
    150
  );
  const primaryContacts = useMemo(() => buildContacts(profile, recruiterName), [profile, recruiterName]);
  const linkedCompanyContacts = useMemo(
    () =>
      normalizedEntityType === "lead"
        ? buildContacts(linkedCompanyProfile || {}, linkedCompanyRecruiterName)
        : [],
    [linkedCompanyProfile, linkedCompanyRecruiterName, normalizedEntityType]
  );
  const fetchedCompanyContacts = useMemo(
    () =>
      dedupeHiringManagerRows(hiringManagers).filter(
        (item) => !isRecruiterLikeEntry(item, recruiterName) && isCompanyContactRow(item)
      ),
    [hiringManagers, recruiterName]
  );
  const linkedFetchedCompanyContacts = useMemo(
    () =>
      normalizedEntityType === "lead"
        ? dedupeHiringManagerRows(linkedCompanyHiringManagers).filter(
            (item) =>
              !isRecruiterLikeEntry(item, linkedCompanyRecruiterName) &&
              isCompanyContactRow(item)
          )
        : [],
    [linkedCompanyHiringManagers, linkedCompanyRecruiterName, normalizedEntityType]
  );
  const linkedCompanyOnlyContacts = useMemo(
    () =>
      mergeUniqueContacts([
        linkedCompanyContacts,
        linkedFetchedCompanyContacts,
      ]),
    [linkedCompanyContacts, linkedFetchedCompanyContacts]
  );
  const contacts = useMemo(
    () =>
      mergeUniqueContacts([
        primaryContacts,
        fetchedCompanyContacts,
        linkedCompanyContacts,
        linkedFetchedCompanyContacts,
      ]),
    [
      fetchedCompanyContacts,
      linkedCompanyContacts,
      linkedFetchedCompanyContacts,
      primaryContacts,
    ]
  );
  const jobsSourceProfile = useMemo(() => {
    if (normalizedEntityType !== "lead") return profile;
    if (linkedCompanyProfile && typeof linkedCompanyProfile === "object") {
      return linkedCompanyProfile;
    }
    return linkedCompanyLoading ? {} : profile;
  }, [linkedCompanyLoading, linkedCompanyProfile, normalizedEntityType, profile]);
  const jobs = useMemo(() => buildJobs(jobsSourceProfile), [jobsSourceProfile]);
  const fallbackHiringManagers = useMemo(
    () => buildFallbackHiringManagers(profile, recruiterName),
    [profile, recruiterName]
  );
  const linkedCompanyFallbackHiringManagers = useMemo(
    () =>
      normalizedEntityType === "lead"
        ? buildFallbackHiringManagers(linkedCompanyProfile || {}, linkedCompanyRecruiterName)
        : [],
    [linkedCompanyProfile, linkedCompanyRecruiterName, normalizedEntityType]
  );
  const sanitizedHiringManagers = useMemo(
    () =>
      dedupeHiringManagerRows(hiringManagers).filter(
        (item) => !isRecruiterLikeEntry(item, recruiterName) && isHiringManagerContactRow(item)
      ),
    [hiringManagers, recruiterName]
  );
  const linkedSanitizedHiringManagers = useMemo(
    () =>
      normalizedEntityType === "lead"
        ? dedupeHiringManagerRows(linkedCompanyHiringManagers).filter(
            (item) =>
              !isRecruiterLikeEntry(item, linkedCompanyRecruiterName) &&
              isHiringManagerContactRow(item)
          )
        : [],
    [linkedCompanyHiringManagers, linkedCompanyRecruiterName, normalizedEntityType]
  );
  const linkedCompanyOnlyHiringManagers = useMemo(
    () =>
      dedupeHiringManagerRows([
        ...linkedSanitizedHiringManagers,
        ...linkedCompanyFallbackHiringManagers,
      ]).filter((item) => !isRecruiterLikeEntry(item, linkedCompanyRecruiterName)),
    [
      linkedCompanyFallbackHiringManagers,
      linkedCompanyRecruiterName,
      linkedSanitizedHiringManagers,
    ]
  );
  const mergedHiringManagers = useMemo(
    () =>
      dedupeHiringManagerRows([
        ...sanitizedHiringManagers,
        ...linkedSanitizedHiringManagers,
        ...fallbackHiringManagers,
        ...linkedCompanyFallbackHiringManagers,
      ]).filter((item) => !isRecruiterLikeEntry(item, recruiterName)),
    [
      fallbackHiringManagers,
      linkedCompanyFallbackHiringManagers,
      linkedSanitizedHiringManagers,
      recruiterName,
      sanitizedHiringManagers,
    ]
  );
  const jobsPreview = useMemo(() => jobs.slice(0, 2), [jobs]);
  const contactsPreview = useMemo(() => contacts.slice(0, 3), [contacts]);
  const contactPreviewRows = useMemo(
    () =>
      contactsPreview.map((contact, index) => ({
        contact,
        channelActions: buildContactChannelActions(contact),
        key: `${contact.id ?? contact.name}-${index}`,
      })),
    [contactsPreview]
  );
  const contactsCount = contacts.length;
  const relatedLeads = Array.isArray(profile.related_leads) ? profile.related_leads : [];
  const activities = Array.isArray(profile.activities) ? profile.activities : [];
  const contactsSectionLoading =
    normalizedEntityType === "lead" &&
    linkedCompanyLoading &&
    !primaryContacts.length &&
    !fetchedCompanyContacts.length;
  const hiringManagersSectionLoading =
    hiringManagersLoading ||
    (normalizedEntityType === "lead" &&
      linkedCompanyLoading &&
      !sanitizedHiringManagers.length &&
      !linkedSanitizedHiringManagers.length &&
      !fallbackHiringManagers.length);
  const hiringManagersPreview = useMemo(
    () => mergedHiringManagers.slice(0, 3),
    [mergedHiringManagers]
  );
  const jobsCountRaw = Number(
    jobsSourceProfile?.jobsCount ??
      jobsSourceProfile?.jobs_count ??
      jobsSourceProfile?.jobOpenings ??
      jobsSourceProfile?.job_openings ??
      jobs.length
  );
  const jobsCount = Number.isFinite(jobsCountRaw) && jobsCountRaw > 0
    ? jobsCountRaw
    : jobs.length;

  const handleShowMore = (tab = "details") => {
    if (!canShowMore) return;

    onClose?.();

    const linkedTargetCompanyId =
      linkedCompanyId ?? getLinkedCompanyId(linkedCompanyProfile) ?? getLinkedCompanyId(profile);
    const shouldOpenLinkedCompany =
      normalizedEntityType === "lead" &&
      Number.isFinite(Number(linkedTargetCompanyId)) &&
      Number(linkedTargetCompanyId) > 0;
    const openEntityType = shouldOpenLinkedCompany ? "company" : normalizedEntityType;
    const openEntityId = shouldOpenLinkedCompany ? linkedTargetCompanyId : entityId;
    const originatingLeadId =
      normalizedEntityType === "lead" &&
      Number.isFinite(Number(entityId)) &&
      Number(entityId) > 0
        ? Number(entityId)
        : null;
    const openProfile = shouldOpenLinkedCompany
      ? {
          ...(profile || {}),
          ...(linkedCompanyProfile || {}),
          id: linkedTargetCompanyId,
          company_id: linkedTargetCompanyId,
          companyId: linkedTargetCompanyId,
        }
      : profile;
    const openCompanyName = safeStr(openProfile?.company_name || openProfile?.name, companyName);
    const openCompanyLogo = openProfile?.company_logo || openProfile?.logo || companyLogo;
    const openWebsiteHref = safeStr(
      openProfile?.website || openProfile?.company_website || openProfile?.website_url,
      websiteHref
    );
    const openWebsiteLabel = formatWebsiteLabel(openWebsiteHref);
    const openIndustryText = safeStr(
      openProfile?.industry || openProfile?.industry_sector,
      industryText
    );
    const openStatusText = safeStr(openProfile?.status, statusText);
    const openOwnerName = safeStr(
      openProfile?.owner_name || openProfile?.owner || openProfile?.team_member,
      ownerName
    );
    const openSourceText = safeStr(openProfile?.source, sourceText);
    const openLastActivityTitle = safeStr(
      openProfile?.last_activity_type,
      lastActivityTitle
    );
    const openLastActivityMessage = truncateText(
      openProfile?.last_activity_message ||
        openProfile?.last_activity_detail ||
        openProfile?.full_company_description ||
        (openProfile?.contact_email
          ? `Contact: ${openProfile.contact_email}`
          : openSourceText !== "-"
          ? `Source: ${openSourceText}`
          : ""),
      150
    );
    const openContacts = shouldOpenLinkedCompany
      ? linkedCompanyOnlyContacts
      : contacts;
    const openContactsCountRaw = Number(
      openProfile?.contacts_count ?? openProfile?.contactsCount
    );
    const openContactsCount =
      Number.isFinite(openContactsCountRaw) && openContactsCountRaw > 0
        ? Math.max(openContactsCountRaw, openContacts.length)
        : openContacts.length;
    const openHiringManagers = shouldOpenLinkedCompany
      ? linkedCompanyOnlyHiringManagers
      : mergedHiringManagers;
    const openRelatedLeads = Array.isArray(openProfile?.related_leads)
      ? openProfile.related_leads
      : relatedLeads;
    const openLeadsCountRaw = Number(openProfile?.leads_count ?? openProfile?.leadsCount);
    const openLeadsCount =
      Number.isFinite(openLeadsCountRaw) && openLeadsCountRaw > 0
        ? Math.max(openLeadsCountRaw, openRelatedLeads.length)
        : openRelatedLeads.length;
    const openJobs = shouldOpenLinkedCompany ? buildJobs(openProfile) : jobs;
    const openJobsCountRaw = Number(
      openProfile?.jobs_count ??
        openProfile?.jobsCount ??
        openProfile?.job_openings ??
        openProfile?.jobOpenings
    );
    const openJobsCount =
      Number.isFinite(openJobsCountRaw) && openJobsCountRaw > 0
        ? Math.max(openJobsCountRaw, openJobs.length)
        : openJobs.length;
    const openActivities = Array.isArray(openProfile?.activities)
      ? openProfile.activities
      : activities;
    const openActivitiesCountRaw = Number(
      openProfile?.activities_count ?? openProfile?.activitiesCount
    );
    const openActivitiesCount =
      Number.isFinite(openActivitiesCountRaw) && openActivitiesCountRaw > 0
        ? Math.max(openActivitiesCountRaw, openActivities.length)
        : openActivities.length || (openLastActivityTitle || openLastActivityMessage ? 1 : 0);

    const openCompany = {
      ...openProfile,
      id: openEntityId ?? openProfile?.id,
      name: openCompanyName,
      logo: openCompanyLogo,
      website: openWebsiteHref || openWebsiteLabel,
      industry: openIndustryText,
      status: openStatusText,
      owner: openOwnerName,
      owner_name: safeStr(openProfile?.owner_name || openProfile?.owner || openProfile?.team_member, openOwnerName),
      createdAt: openProfile?.createdAt || openProfile?.created_at || "",
      jobOpenings: openJobsCount,
      jobsCount: openJobsCount,
      jobs_count: openJobsCount,
      jobs: openJobs,
      related_leads: openRelatedLeads,
      leadsCount: openLeadsCount,
      leads_count: openLeadsCount,
      contacts: openContacts,
      contactsCount: openContactsCount,
      contacts_count: openContactsCount,
      hiringManagers: openHiringManagers,
      hiring_managers: openHiringManagers,
      activities: openActivities,
      activitiesCount: openActivitiesCount,
      activities_count: openActivitiesCount,
      source: openSourceText,
      location: safeStr(openProfile?.location),
      last_activity_time:
        openProfile?.last_activity_time || openProfile?.updated_at || openProfile?.created_at,
      last_activity_type: openLastActivityTitle,
      last_activity_message: openLastActivityMessage,
      contact_email: safeStr(openProfile?.contact_email),
      contact_phone: safeStr(openProfile?.contact_phone),
      linkedin_url: safeStr(openProfile?.linkedin_url),
      facebook_url: safeStr(openProfile?.facebook_url),
      twitter_url: safeStr(openProfile?.twitter_url || openProfile?.x_url),
      instagram_url: safeStr(openProfile?.instagram_url),
      youtube_url: safeStr(openProfile?.youtube_url),
      tiktok_url: safeStr(openProfile?.tiktok_url),
      company_id:
        shouldOpenLinkedCompany
          ? openEntityId
          : linkedCompanyId ?? openProfile?.company_id ?? openProfile?.companyId ?? null,
      companyId:
        shouldOpenLinkedCompany
          ? openEntityId
          : linkedCompanyId ?? openProfile?.companyId ?? openProfile?.company_id ?? null,
      lead_id:
        originatingLeadId ??
        (openProfile?.lead_id != null ? Number(openProfile.lead_id) || null : null),
      leadId:
        originatingLeadId ??
        (openProfile?.leadId != null ? Number(openProfile.leadId) || null : null),
      social_links: getCompanySocialLinksArray(openProfile),
      entityType: openEntityType,
    };
    const openCompanyNavEntity = buildOpenCompanyNavEntity(openCompany, {
      entityType: openEntityType,
    });
    if (!openCompanyNavEntity?.id) return;

    if (typeof onShowMore === "function") {
      onShowMore(tab, openCompany);
      return;
    }

    const payload = buildOpenCompanyNavPayload({
      company: openCompany,
      defaultTab: tab,
      entityType: openEntityType,
    });
    if (!payload) return;

    try {
      sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // ignore session storage failures
    }

    const qs = new URLSearchParams();
    if (openEntityId !== null && openEntityId !== undefined) {
      qs.set("companyId", String(openEntityId));
    }
    qs.set("tab", tab);
    qs.set("entityType", openEntityType);

    navigate(`/companies?${qs.toString()}`, { state: payload });
  };

  const quickActions = useMemo(() => {
    const NON_LINKEDIN_SOCIAL = ["facebook", "twitter", "x", "google", "instagram", "youtube", "tiktok"];
    return buildCompanyProfileActions(profile).filter(
      (action) => !NON_LINKEDIN_SOCIAL.includes(action?.type)
    );
  }, [profile]);

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="company-profile-drawer"
      width={320}
      closeColor="#9C4FFF"
      paperSx={{
        borderRadius: "18px 0 0 18px",
        boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
      }}
      headerLeft={
        <Box display="flex" alignItems="center" gap={1}>
          <Eye size="18" color="#3451F4" variant="Linear" />
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#3451F4",
            }}
          >
            Company Profile
          </Typography>
        </Box>
      }
    >
      <Box sx={{ minHeight: 120 }}>
        <Box
          mt={0.25}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
        >
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: "#3451F4" }}
          >
            Hiring Team
          </Typography>

          <Box textAlign="right">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              gap={0.75}
            >
              <Typography sx={{ fontSize: 11, color: "#111827" }}>
                Owner:
              </Typography>
              <Chip
                label={ownerName}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  backgroundColor: "#EAF7FF",
                  color: "#5B6677",
                  borderRadius: 999,
                  "& .MuiChip-label": { px: 1.1 },
                }}
              />
            </Box>

            <Typography sx={{ mt: 0.4, fontSize: 10.5, color: "#A2ABB4" }}>
              {createdLabel}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2.25, borderColor: "#E7ECF2" }} />

        <Box display="flex" alignItems="center" gap={1.25}>
          <Avatar
            src={companyLogo || undefined}
            sx={{
              width: 48,
              height: 48,
              bgcolor: "#2F6FFF",
              fontSize: 18,
              fontWeight: 700,
              color: "#fff",
            }}
          >
            {companyName?.[0] || "C"}
          </Avatar>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}
              noWrap
            >
              {companyName}
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: "#9C4FFF", fontWeight: 500 }}
              noWrap
            >
              {websiteLabel}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: "#98A2B3" }} noWrap>
              {industryText}
            </Typography>
            <Box
              sx={{
                mt: 0.45,
                display: "flex",
                alignItems: "center",
                gap: 0.6,
                minWidth: 0,
              }}
            >
              <Typography sx={{ fontSize: 11.5, color: "#111827", fontWeight: 500 }}>
                Status:
              </Typography>
              <Typography
                sx={{ fontSize: 11.5, color: "#9C4FFF", fontWeight: 600, minWidth: 0 }}
                noWrap
              >
                {statusText}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box mt={2.8}>
          <Typography
            sx={{ fontSize: 16, fontWeight: 700, color: "#3451F4", lineHeight: 1.2 }}
          >
            Quick Actions:
          </Typography>

          <Box mt={1.25}>
            <InfoActionIcons
              actions={quickActions}
              gap={1.25}
              showDisabled={true}
              variant="filled"
              filledWidth={48}
              filledHeight={34}
              iconSize={16}
              showGroupDivider={false}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "#E7ECF2" }} />

        <Box>
          <Typography
            sx={{ fontSize: 12, fontWeight: 600, color: "#3451F4" }}
          >
            Last Activity:
          </Typography>

          <Box mt={1} display="flex" alignItems="center" gap={0.75}>
            <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: "#A2ABB4" }} />
            <Typography sx={{ fontSize: 12, color: "#98A2B3" }}>
              {lastActivityTime}
            </Typography>
          </Box>

          <Box
            mt={1.1}
            display="flex"
            justifyContent="space-between"
            alignItems="flex-end"
            gap={1.5}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 12.5, color: "#374151", lineHeight: 1.45 }}
              >
                {lastActivityTitle}
              </Typography>
              {lastActivityMessage ? (
                <Typography
                  sx={{ mt: 0.45, fontSize: 12, color: "#4B5563", lineHeight: 1.45 }}
                >
                  {lastActivityMessage}
                </Typography>
              ) : null}
            </Box>

            {canShowMore ? (
              <Typography
                sx={{
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#9C4FFF",
                  cursor: "pointer",
                }}
                onClick={() => handleShowMore("activities")}
              >
                View
              </Typography>
            ) : null}
          </Box>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "#E7ECF2" }} />

        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: "#3451F4" }}
            >
              Jobs ({jobsCount})
            </Typography>
            {canShowMore ? (
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#9C4FFF",
                  cursor: "pointer",
                }}
                onClick={() => handleShowMore("jobs")}
              >
                View all
              </Typography>
            ) : null}
          </Box>

          <Box mt={1}>
            {jobsPreview.length ? (
              jobsPreview.map((job, index) => (
                <Typography
                  key={`${job.title}-${index}`}
                  sx={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55 }}
                >
                  {job.title}
                  {job.location ? ` - ${job.location}` : ""}
                </Typography>
              ))
            ) : (
              <Typography sx={{ fontSize: 12, color: "#98A2B3" }}>
                No jobs added yet.
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "#E7ECF2" }} />

         <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: "#3451F4" }}
            >
             {`Contacts (${contactsCount})`}
            </Typography>

        <Box>
          <Box
            sx={{
              mt: 0.4,
              display: "grid",
              gridTemplateColumns: "1.2fr 1fr auto",
              columnGap: 1,
              pb: 0.9,
              borderBottom: "1px solid #E7ECF2",
            }}
          >
            <Typography sx={{ fontSize: 10.5, color: "#A2ABB4" }}>
              Name
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: "#A2ABB4" }}>
              Role
            </Typography>
            <Typography
              sx={{ fontSize: 10.5, color: "#A2ABB4", textAlign: "right" }}
            >
              Channels
            </Typography>
          </Box>

          {contactsSectionLoading ? (
            <Box sx={{ py: 1.4 }}>
              <Typography sx={{ fontSize: 12, color: "#98A2B3" }}>
                Loading contacts...
              </Typography>
            </Box>
          ) : contactPreviewRows.length ? (
            contactPreviewRows.map(({ contact, channelActions, key }, index) => {
              return (
                <Box
                  key={key}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 1fr auto",
                    columnGap: 1,
                    alignItems: "center",
                    py: 1,
                    borderBottom:
                      index === contactPreviewRows.length - 1
                        ? "none"
                        : "1px solid #E7ECF2",
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: "#374151" }}>
                    {contact.name}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                    {contact.role}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 1,
                      minHeight: 24,
                    }}
                  >
                    {channelActions.length ? (
                      <>
                        {channelActions.map((action) => (
                          <InfoActionIcons
                            key={`${contact.name}-${action.type}`}
                            actions={[action]}
                            gap={0}
                            showGroupDivider={false}
                            iconSize={18}
                          />
                        ))}
                      </>
                    ) : (
                      <Typography sx={{ fontSize: 11.5, color: "#98A2B3" }}>
                        -
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Box sx={{ py: 1.4 }}>
              <Typography sx={{ fontSize: 12, color: "#98A2B3" }}>
                No contacts added yet.
              </Typography>
            </Box>
          )}

          {canShowMore ? (
            <Box
              mt={0.9}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#9C4FFF",
                  cursor: "pointer",
                }}
                onClick={() => handleShowMore("contacts")}
              >
                Add Contact
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#D0D5DD" }}>|</Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#9C4FFF",
                  cursor: "pointer",
                }}
                onClick={() => handleShowMore("contacts")}
              >
                View all
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "#E7ECF2" }} />

        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: "#3451F4" }}
            >
              Hiring Managers
            </Typography>
            {canShowMore ? (
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#9C4FFF",
                  cursor: "pointer",
                }}
                onClick={() => handleShowMore("details")}
              >
                View all
              </Typography>
            ) : null}
          </Box>

          <Box
            sx={{
              mt: 1.25,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: 1,
              pb: 0.9,
              borderBottom: "1px solid #E7ECF2",
            }}
          >
            <Typography sx={{ fontSize: 10.5, color: "#A2ABB4" }}>
              Name
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: "#A2ABB4" }}>
              Role
            </Typography>
          </Box>

          {hiringManagersSectionLoading ? (
            <Box sx={{ py: 1.4 }}>
              <Typography sx={{ fontSize: 12, color: "#98A2B3" }}>
                Loading hiring managers...
              </Typography>
            </Box>
          ) : hiringManagersPreview.length ? (
            hiringManagersPreview.map((manager, index) => (
              <Box
                key={`${manager.id ?? manager.name}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  columnGap: 1,
                  alignItems: "center",
                  py: 1,
                  borderBottom:
                    index === hiringManagersPreview.length - 1
                      ? "none"
                      : "1px solid #E7ECF2",
                }}
              >
                <Typography sx={{ fontSize: 12, color: "#374151" }}>
                  {manager.name}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                  {manager.role}
                </Typography>
              </Box>
            ))
          ) : (
            <Box sx={{ py: 1.4 }}>
              <Typography sx={{ fontSize: 12, color: "#98A2B3" }}>
                No hiring managers added yet.
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2.5, borderColor: "#E7ECF2" }} />

        <Box>
          <Typography sx={{ fontSize: 11, color: "#98A2B3", mb: 0.7 }}>
            Source
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={sourceText}
            InputProps={{ readOnly: true }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "7px",
                fontSize: 12,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#D7DEE8",
                },
              },
            }}
          />
        </Box>

        {(loading || error) && (
          <Box
            mt={1.5}
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
          >
            {loading ? <CircularProgress size={14} /> : null}
            <Typography sx={{ fontSize: 11, color: "#98A2B3" }}>
              {loading ? "Refreshing company data..." : error}
            </Typography>
          </Box>
        )}

        {canShowMore ? (
          <Box mt={2.4} display="flex" justifyContent="center">
            <Button
              variant="contained"
              onClick={() => handleShowMore("details")}
              sx={{
                minWidth: 96,
                height: 28,
                textTransform: "none",
                fontSize: 11,
                borderRadius: "999px",
                outline: "none",
                backgroundColor: "#9C4FFF",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#8C3EFF",
                  boxShadow: "none",
                },
                "&:focus, &:focus-visible, &.Mui-focusVisible": {
                  outline: "none",
                  boxShadow: "none",
                },
              }}
            >
              Show more
            </Button>
          </Box>
        ) : null}
      </Box>
    </ReusableRightDrawer>
  );
}
